import { ref, computed, watchEffect, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

/**
 * v2.0.20: Composable for dynamic button color customization
 * Follows the same dual-layer pattern as useSidebarStyle:
 *   Global (InstallationConfig PLATFORM_STYLE.buttons) + Per-Account (custom_attributes.sidebar_style.buttons)
 *   Account override > Global > null (system defaults).
 *
 * Strategy: Since bg-n-brand is a hardcoded Tailwind color (#2781F6), not a CSS variable,
 * we inject a <style> tag into <head> to override button colors via CSS custom properties.
 */

const STYLE_TAG_ID = 'platform-button-style';

// CSS variable names for button customization
const CSS_VAR_MAP = {
  brand_btn_bg: '--btn-brand-bg',
  brand_btn_text: '--btn-brand-text',
  editor_btn_bg: '--btn-editor-bg',
  editor_btn_text: '--btn-editor-text',
  accordion_btn_bg: '--btn-accordion-bg',
  accordion_btn_text: '--btn-accordion-text',
  accordion_icon_color: '--btn-accordion-icon',
};

const BUTTON_STYLE_KEYS = Object.keys(CSS_VAR_MAP);

/**
 * Detect dark mode (mirrors useSidebarStyle logic).
 * themeHelper.js toggles .dark on <body>.
 */
function detectDarkMode() {
  if (document.body?.classList.contains('dark')) return true;
  const colorScheme =
    document.documentElement.style.getPropertyValue('color-scheme');
  if (colorScheme === 'dark') return true;
  return false;
}

/**
 * Parse PLATFORM_STYLE.buttons from globalConfig.
 * globalConfig is static after page load,so parsed once.
 */
function parseGlobalButtonStyle() {
  try {
    const raw = window.globalConfig?.PLATFORM_STYLE;
    if (!raw) return null;

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.enabled) return null;

    const buttons = parsed.buttons;
    return buttons && typeof buttons === 'object' ? buttons : null;
  } catch {
    return null;
  }
}

/**
 * Parse PLATFORM_STYLE.background from globalConfig.
 */
function parseGlobalBackgroundStyle() {
  try {
    const raw = window.globalConfig?.PLATFORM_STYLE;
    if (!raw) return null;

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.enabled) return null;

    const bg = parsed.background;
    return bg && typeof bg === 'object' ? bg : null;
  } catch {
    return null;
  }
}

/**
 * Parse PLATFORM_STYLE.bubbles from globalConfig.
 */
function parseGlobalBubbleStyle() {
  try {
    const raw = window.globalConfig?.PLATFORM_STYLE;
    if (!raw) return null;

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.enabled) return null;

    const bubbles = parsed.bubbles;
    return bubbles && typeof bubbles === 'object' ? bubbles : null;
  } catch {
    return null;
  }
}

/**
 * Parse PLATFORM_STYLE.cards from globalConfig.
 */
function parseGlobalCardStyle() {
  try {
    const raw = window.globalConfig?.PLATFORM_STYLE;
    if (!raw) return null;

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.enabled) return null;

    const cards = parsed.cards;
    return cards && typeof cards === 'object' ? cards : null;
  } catch {
    return null;
  }
}

/**
 * Parse PLATFORM_STYLE.kanban from globalConfig.
 */
function parseGlobalKanbanStyle() {
  try {
    const raw = window.globalConfig?.PLATFORM_STYLE;
    if (!raw) return null;

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.enabled) return null;

    const kanban = parsed.kanban;
    return kanban && typeof kanban === 'object' ? kanban : null;
  } catch {
    return null;
  }
}

/**
 * Validate a URL string for safe CSS injection.
 * Only allows http/https URLs without quotes, HTML tags, backslashes, or parentheses.
 */
function isValidCssUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /^https?:\/\/[^\s'"<>()\\]+$/.test(url);
}

/**
 * Generate the CSS override stylesheet content.
 * Uses CSS custom properties on :root plus targeted selectors for brand and editor buttons.
 */
function generateStyleSheet(colors, backgroundColors, bubbleColors, cardColors, kanbanColors) {
  if (!colors && !backgroundColors && !bubbleColors && !cardColors && !kanbanColors) return '';

  const vars = [];
  if (colors) {
    BUTTON_STYLE_KEYS.forEach(key => {
      const value = colors[key];
      const cssVar = CSS_VAR_MAP[key];
      if (value && cssVar && typeof value === 'string') {
        vars.push(`  ${cssVar}: ${value};`);
      }
    });
  }

  if (vars.length === 0 && !backgroundColors && !bubbleColors && !cardColors && !kanbanColors) return '';

  const rules = vars.length > 0 ? [`:root {\n${vars.join('\n')}\n}`] : [];

  // Brand button overrides (bg-n-brand = solid blue buttons: Create, Update, Download, etc.)
  if (colors?.brand_btn_bg) {
    rules.push(`
/* Brand button background override */
.bg-n-brand {
  background-color: var(--btn-brand-bg) !important;
}
/* Brand button faded variant */
.bg-n-brand\\/10 {
  background-color: color-mix(in srgb, var(--btn-brand-bg) 10%, transparent) !important;
}
.hover\\:enabled\\:bg-n-brand\\/20:hover:enabled {
  background-color: color-mix(in srgb, var(--btn-brand-bg) 20%, transparent) !important;
}
.focus-visible\\:bg-n-brand\\/20:focus-visible {
  background-color: color-mix(in srgb, var(--btn-brand-bg) 20%, transparent) !important;
}
/* Brand button outline variant */
.outline-n-brand {
  outline-color: var(--btn-brand-bg) !important;
}`);
  }

  if (colors?.brand_btn_text) {
    rules.push(`
/* Brand button text color override — solid variant (text-white) */
.bg-n-brand.text-white {
  color: var(--btn-brand-text) !important;
}
/* Brand button text color override — faded and outline variants (text-n-blue-text) */
.bg-n-brand\\/10.text-n-blue-text,
.text-n-blue-text.outline-n-brand {
  color: var(--btn-brand-text) !important;
}`);
  }

  // Editor button overrides (slate faded buttons in reply panel: Emoji, Attach, Meeting, AI)
  if (colors?.editor_btn_bg) {
    rules.push(`
/* Editor buttons background override (slate faded variant in left-wrap) */
.left-wrap .bg-n-slate-9\\/10 {
  background-color: var(--btn-editor-bg) !important;
}
.left-wrap .hover\\:enabled\\:bg-n-slate-9\\/20:hover:enabled {
  background-color: color-mix(in srgb, var(--btn-editor-bg) 80%, transparent) !important;
}`);
  }

  if (colors?.editor_btn_text) {
    rules.push(`
/* Editor buttons text/icon color override */
.left-wrap .text-n-slate-12 {
  color: var(--btn-editor-text) !important;
}`);
  }

  // Accordion overrides (contact sidebar accordion headers in .list-group)
  // IMPORTANT: target `button.bg-n-slate-2` (not just `.bg-n-slate-2`) to avoid
  // colliding with CustomAttributes.vue alternating row stripes that also use bg-n-slate-2
  if (colors?.accordion_btn_bg) {
    rules.push(`
/* Accordion header background override */
.list-group button.bg-n-slate-2 {
  background-color: var(--btn-accordion-bg) !important;
}`);
  }

  if (colors?.accordion_btn_text) {
    rules.push(`
/* Accordion header title text color override */
.list-group h5.text-n-slate-12 {
  color: var(--btn-accordion-text) !important;
}`);
  }

  if (colors?.accordion_icon_color) {
    rules.push(`
/* Accordion expand/collapse icon color override */
.list-group .text-n-blue-text {
  color: var(--btn-accordion-icon) !important;
}`);
  }

  // Conversation panel background (bg_color + bg_image)
  if (backgroundColors) {
    const bgColor = backgroundColors.bg_color;
    const bgImage = backgroundColors.bg_image;
    const isValidHex = v =>
      typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);

    if ((bgColor && isValidHex(bgColor)) || (bgImage && isValidCssUrl(bgImage))) {
      let bgCss = '.conversation-panel {\n';
      if (bgColor && isValidHex(bgColor)) {
        bgCss += `  background-color: ${bgColor} !important;\n`;
      }
      if (bgImage && isValidCssUrl(bgImage)) {
        bgCss += `  background-image: url("${bgImage}") !important;\n`;
        bgCss += '  background-repeat: repeat !important;\n';
        bgCss += '  background-size: auto !important;\n';
        bgCss += '  background-position: center !important;\n';
      }
      bgCss += '}';
      rules.push(`\n/* Conversation panel background override */\n${bgCss}`);
    }
  }

  // Message bubble color overrides
  if (bubbleColors) {
    const isValidHex = v =>
      typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
    const rightBg = bubbleColors.right_bubble_bg;
    const leftBg = bubbleColors.left_bubble_bg;
    const privateBg = bubbleColors.private_bg;

    // Right bubble (agent messages): .right-bubble.bg-n-solid-blue
    if (rightBg && isValidHex(rightBg)) {
      rules.push(`
/* Right bubble (sent messages) background override */
.right-bubble.bg-n-solid-blue {
  background-color: ${rightBg} !important;
}`);
    }

    // Left bubble (user messages): .left-bubble.bg-n-slate-4
    if (leftBg && isValidHex(leftBg)) {
      rules.push(`
/* Left bubble (received messages) background override */
.left-bubble.bg-n-slate-4 {
  background-color: ${leftBg} !important;
}`);
    }

    // Private note bubbles + editor
    if (privateBg && isValidHex(privateBg)) {
      rules.push(`
/* Private note bubbles background override */
.right-bubble.bg-n-solid-amber,
.left-bubble.bg-n-solid-amber {
  background-color: ${privateBg} !important;
}
/* Private note editor (reply box) background override */
.reply-box.is-private {
  background-color: ${privateBg} !important;
}`);
    }
  }

  // Conversation card background and border overrides
  if (cardColors) {
    const isValidHex = v =>
      typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);

    // Lighten a hex color by a given amount (0-1). Blends toward white.
    const lightenHex = (hex, amount) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      const lr = Math.min(255, Math.round(r + (255 - r) * amount));
      const lg = Math.min(255, Math.round(g + (255 - g) * amount));
      const lb = Math.min(255, Math.round(b + (255 - b) * amount));
      return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
    };

    const cardBg = cardColors.card_bg;
    const cardBorder = cardColors.card_border;

    if (cardBg && isValidHex(cardBg)) {
      const hoverBg = lightenHex(cardBg, 0.15);
      rules.push(`
/* Conversation card background override — excludes active + selected states */
.conversation.bg-n-solid-2:not(.active) {
  background-color: ${cardBg} !important;
}
.dark .conversation:not(.active) {
  background-color: ${cardBg} !important;
}
/* Auto-generated hover (15% lighter) */
.conversation.bg-n-solid-2:not(.active):hover {
  background-color: ${hoverBg} !important;
}
.dark .conversation:not(.active):hover {
  background-color: ${hoverBg} !important;
}`);
    }

    if (cardBorder && isValidHex(cardBorder)) {
      const hoverBorder = lightenHex(cardBorder, 0.2);
      rules.push(`
/* Conversation card border override — excludes active state */
.conversation.border-n-weak:not(.active) {
  border-color: ${cardBorder} !important;
}
.dark .conversation:not(.active) {
  border-color: ${cardBorder} !important;
}
/* Auto-generated hover border (20% lighter) */
.conversation.border-n-weak:not(.active):hover {
  border-color: ${hoverBorder} !important;
}
.dark .conversation:not(.active):hover {
  border-color: ${hoverBorder} !important;
}`);
    }
  }

  // Kanban stage/card color overrides
  if (kanbanColors) {
    const isValidHex = v =>
      typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
    const stageHeaderBg = kanbanColors.stage_header_bg;
    const stageBg = kanbanColors.stage_bg;
    const kanbanCardBg = kanbanColors.kanban_card_bg;
    const kanbanCardBorder = kanbanColors.kanban_card_border;

    // Stage header background (the div with borderLeft inside .kanban-column-root)
    if (stageHeaderBg && isValidHex(stageHeaderBg)) {
      rules.push(`
/* Kanban stage header background override */
.kanban-column-root > div:first-child {
  background-color: ${stageHeaderBg} !important;
}`);
    }

    // Stage body background (the scrollable area below the header — 2nd direct child)
    if (stageBg && isValidHex(stageBg)) {
      rules.push(`
/* Kanban stage body background override */
.kanban-column-root > div:nth-child(2) {
  background-color: ${stageBg} !important;
}`);
    }

    // Kanban card background + border (targets KanbanItem root div inside wrapper)
    if (kanbanCardBg && isValidHex(kanbanCardBg)) {
      rules.push(`
/* Kanban card background override */
.kanban-item-wrapper > div[class] {
  background-color: ${kanbanCardBg} !important;
}`);
    }

    if (kanbanCardBorder && isValidHex(kanbanCardBorder)) {
      rules.push(`
/* Kanban card border override */
.kanban-item-wrapper > div[class] {
  border-color: ${kanbanCardBorder} !important;
}`);
    }
  }

  return rules.join('\n');
}

/**
 * Inject or update the <style> tag in <head>.
 */
function injectStyleTag(cssContent) {
  let styleTag = document.getElementById(STYLE_TAG_ID);

  if (!cssContent) {
    // Remove style tag if no custom styles
    if (styleTag) styleTag.remove();
    return;
  }

  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = STYLE_TAG_ID;
    styleTag.setAttribute('data-source', 'platform-button-customization');
    document.head.appendChild(styleTag);
  }

  styleTag.textContent = cssContent;
}

export function useButtonStyle() {
  const store = useStore();
  const route = useRoute();

  // Reactive dark mode detection (same pattern as useSidebarStyle)
  const isDark = ref(detectDarkMode());

  // Parse globalConfig once at init (static after page load)
  const globalButtonStyle = ref(parseGlobalButtonStyle());

  // Account-level button style override
  const accountButtonStyle = computed(() => {
    const accountId = route.params?.accountId;
    if (!accountId) return null;

    const account = store.getters['accounts/getAccount'](accountId);
    if (!account || typeof account !== 'object') return null;

    const sidebarStyle = account.custom_attributes?.sidebar_style;
    if (!sidebarStyle || !sidebarStyle.enabled) return null;

    const buttons = sidebarStyle.buttons;
    return buttons && typeof buttons === 'object' ? buttons : null;
  });

  // Merged: account > global > null
  const effectiveButtonStyle = computed(() => {
    return accountButtonStyle.value || globalButtonStyle.value || null;
  });

  // Current theme colors based on dark/light mode
  const currentButtonColors = computed(() => {
    const style = effectiveButtonStyle.value;
    if (!style) return null;

    const theme = isDark.value ? 'dark' : 'light';
    const colors = style[theme];
    return colors && typeof colors === 'object' ? colors : null;
  });

  // Parse globalConfig once for background
  const globalBackgroundStyle = ref(parseGlobalBackgroundStyle());

  // Account-level background override
  const accountBackgroundStyle = computed(() => {
    const accountId = route.params?.accountId;
    if (!accountId) return null;

    const account = store.getters['accounts/getAccount'](accountId);
    if (!account || typeof account !== 'object') return null;

    const sidebarStyle = account.custom_attributes?.sidebar_style;
    if (!sidebarStyle || !sidebarStyle.enabled) return null;

    const bg = sidebarStyle.background;
    return bg && typeof bg === 'object' ? bg : null;
  });

  // Merged: account > global > null
  const effectiveBackgroundStyle = computed(() => {
    return accountBackgroundStyle.value || globalBackgroundStyle.value || null;
  });

  // Current background based on dark/light mode
  const currentBackgroundColors = computed(() => {
    const style = effectiveBackgroundStyle.value;
    if (!style) return null;

    const theme = isDark.value ? 'dark' : 'light';
    const colors = style[theme];
    return colors && typeof colors === 'object' ? colors : null;
  });

  // Parse globalConfig once for bubble styles
  const globalBubbleStyle = ref(parseGlobalBubbleStyle());

  // Account-level bubble style override
  const accountBubbleStyle = computed(() => {
    const accountId = route.params?.accountId;
    if (!accountId) return null;

    const account = store.getters['accounts/getAccount'](accountId);
    if (!account || typeof account !== 'object') return null;

    const sidebarStyle = account.custom_attributes?.sidebar_style;
    if (!sidebarStyle || !sidebarStyle.enabled) return null;

    const bubbles = sidebarStyle.bubbles;
    return bubbles && typeof bubbles === 'object' ? bubbles : null;
  });

  // Merged: account > global > null
  const effectiveBubbleStyle = computed(() => {
    return accountBubbleStyle.value || globalBubbleStyle.value || null;
  });

  // Current bubble colors based on dark/light mode
  const currentBubbleColors = computed(() => {
    const style = effectiveBubbleStyle.value;
    if (!style) return null;

    const theme = isDark.value ? 'dark' : 'light';
    const colors = style[theme];
    return colors && typeof colors === 'object' ? colors : null;
  });

  // Parse globalConfig once for card styles
  const globalCardStyle = ref(parseGlobalCardStyle());

  // Account-level card style override
  const accountCardStyle = computed(() => {
    const accountId = route.params?.accountId;
    if (!accountId) return null;

    const account = store.getters['accounts/getAccount'](accountId);
    if (!account || typeof account !== 'object') return null;

    const sidebarStyle = account.custom_attributes?.sidebar_style;
    if (!sidebarStyle || !sidebarStyle.enabled) return null;

    const cards = sidebarStyle.cards;
    return cards && typeof cards === 'object' ? cards : null;
  });

  // Merged: account > global > null
  const effectiveCardStyle = computed(() => {
    return accountCardStyle.value || globalCardStyle.value || null;
  });

  // Current card colors based on dark/light mode
  const currentCardColors = computed(() => {
    const style = effectiveCardStyle.value;
    if (!style) return null;

    const theme = isDark.value ? 'dark' : 'light';
    const cardThemeColors = style[theme];
    return cardThemeColors && typeof cardThemeColors === 'object' ? cardThemeColors : null;
  });

  // Parse globalConfig once for kanban styles
  const globalKanbanStyle = ref(parseGlobalKanbanStyle());

  // Account-level kanban style override
  const accountKanbanStyle = computed(() => {
    const accountId = route.params?.accountId;
    if (!accountId) return null;

    const account = store.getters['accounts/getAccount'](accountId);
    if (!account || typeof account !== 'object') return null;

    const sidebarStyle = account.custom_attributes?.sidebar_style;
    if (!sidebarStyle || !sidebarStyle.enabled) return null;

    const kanban = sidebarStyle.kanban;
    return kanban && typeof kanban === 'object' ? kanban : null;
  });

  // Merged: account > global > null
  const effectiveKanbanStyle = computed(() => {
    return accountKanbanStyle.value || globalKanbanStyle.value || null;
  });

  // Current kanban colors based on dark/light mode
  const currentKanbanColors = computed(() => {
    const style = effectiveKanbanStyle.value;
    if (!style) return null;

    const theme = isDark.value ? 'dark' : 'light';
    const kanbanThemeColors = style[theme];
    return kanbanThemeColors && typeof kanbanThemeColors === 'object' ? kanbanThemeColors : null;
  });

  watchEffect(() => {
    const colors = currentButtonColors.value;
    const bgColors = currentBackgroundColors.value;
    const bubColors = currentBubbleColors.value;
    const cardCols = currentCardColors.value;
    const kanbanCols = currentKanbanColors.value;
    const css = generateStyleSheet(colors, bgColors, bubColors, cardCols, kanbanCols);
    injectStyleTag(css);
  });

  // Dark mode observers (same pattern as useSidebarStyle)
  const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
  const themeChangeHandler = () => {
    isDark.value = detectDarkMode();
  };
  mediaQuery?.addEventListener?.('change', themeChangeHandler);

  const bodyObserver = new MutationObserver(() => {
    isDark.value = detectDarkMode();
  });
  if (document.body) {
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  const htmlObserver = new MutationObserver(() => {
    isDark.value = detectDarkMode();
  });
  htmlObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style'],
  });

  // Cleanup
  onUnmounted(() => {
    mediaQuery?.removeEventListener?.('change', themeChangeHandler);
    bodyObserver.disconnect();
    htmlObserver.disconnect();
    injectStyleTag(null); // Remove style tag
  });

  return {
    currentButtonColors,
    effectiveButtonStyle,
    currentBackgroundColors,
    effectiveBackgroundStyle,
    currentBubbleColors,
    effectiveBubbleStyle,
    currentCardColors,
    effectiveCardStyle,
    currentKanbanColors,
    effectiveKanbanStyle,
  };
}
